// Mock data for Prakash Electrodes website

export const company = {
  name: 'Prakash Electrodes',
  fullName: 'Prakash Electrodes Private Limited',
  tagline: 'Crafted for the Perfect Weld',
  since: 1985,
  location: 'Kota, Rajasthan',
  phone: '+91 77371 00051',
  email: 'prakashelectrodes@gmail.com',
  address: 'F-147 IPIA, Road no.5, Kota, Rajasthan, India',
  logo: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/oso97mfb_logo1-removebg-preview.png',
};

export const blogPosts = [
  {
    id: 'choosing-e6013',
    category: 'Guides',
    author: 'Rakesh Prakash',
    authorRole: 'Head of Quality, Prakash Electrodes',
    title: 'How to choose the right E6013 electrode for your project',
    excerpt:
      'A practical breakdown of coating types, amperage ranges, and joint preparation to help welders pick the perfect rod every time.',
    date: 'Jun 12, 2025',
    readTime: '6 min read',
    image:
      'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhcmMlMjB3ZWxkaW5nfGVufDB8fHx8MTc4NDQ0MzE4Mnww&ixlib=rb-4.1.0&q=85',
    content: [
      { type: 'p', text: 'E6013 is one of the most versatile mild-steel electrodes available today. It runs on both AC and DC, strikes an arc easily, and produces a smooth, medium-penetration weld that is forgiving to beginners and reliable in the hands of experts. But even within the E6013 family, choosing the right rod for your job makes the difference between a strong weld and one that cracks under load.' },
      { type: 'h2', text: 'Understand the coating' },
      { type: 'p', text: 'The middle digits in the AWS classification (E60-13) indicate the flux coating. A high-titania potassium coating like the one used in our WELDARC and CUTWELD brands gives a stable, quiet arc with very little spatter. This is ideal for sheet metal, thin sections, and cosmetic work where post-weld cleanup matters.' },
      { type: 'h2', text: 'Match the diameter to the joint' },
      { type: 'list', items: [
        '2.5 mm for sheet metal 2\u20133 mm thick, running at 60\u201390 A.',
        '3.15 mm for general fabrication on 4\u20136 mm plate at 90\u2013130 A.',
        '4.0 mm for structural work on 6\u201310 mm plate at 130\u2013180 A.',
        '5.0 mm for heavy fill passes on 10 mm+ plate at 180\u2013230 A.',
      ] },
      { type: 'h2', text: 'Prepare the joint properly' },
      { type: 'p', text: 'Even the finest electrode cannot overcome poor joint preparation. Grind mill scale, rust, and paint off the weld zone. Bevel plates thicker than 6 mm to a 60\u00b0 included angle and leave a 1\u20132 mm root gap. A clean, well-prepared joint lets the flux do its job.' },
      { type: 'h2', text: 'Store your rods dry' },
      { type: 'p', text: 'E6013 electrodes absorb ambient moisture quickly. Store them in a dry cabinet at 20\u201330 \u00b0C. If they have been exposed to humid air, re-dry at 100\u2013120 \u00b0C for one hour before use. Damp flux causes porosity, pinholes, and arc instability \u2014 the three most common defects we hear about from the field.' },
      { type: 'quote', text: 'Choosing the right E6013 is 20% about the rod and 80% about the joint, the operator, and the setup. Get all three right and the weld looks after itself.' },
      { type: 'p', text: 'If you are unsure which of our five brands fits your application, our technical team is happy to help. Send us the base metal, thickness, and joint type \u2014 we will recommend the right rod and current settings for your work.' },
    ],
  },
  {
    id: 'low-spatter-tips',
    category: 'Tips & Tricks',
    author: 'Suresh Nagar',
    authorRole: 'Production Manager, Prakash Electrodes',
    title: '5 techniques for cleaner, low-spatter arc welding',
    excerpt:
      'From electrode angle to current selection \u2014 small habits that produce visibly better welds and reduce post-weld cleanup.',
    date: 'May 28, 2025',
    readTime: '4 min read',
    image:
      'https://images.pexels.com/photos/28951817/pexels-photo-28951817.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    content: [
      { type: 'p', text: 'Spatter is more than a cosmetic problem. It slows down production, wastes electrode material, and forces welders to spend extra hours grinding and cleaning. Here are five field-tested techniques our team has refined over 40 years of manufacturing electrodes.' },
      { type: 'h2', text: '1. Set the correct current' },
      { type: 'p', text: 'Amperage that is too high blasts molten metal out of the puddle in every direction. Follow the manufacturer\u2019s recommended range printed on the box and start at the lower end. A stable, steady arc that hisses rather than crackles is the sound you want.' },
      { type: 'h2', text: '2. Hold a consistent arc length' },
      { type: 'p', text: 'An arc length of roughly one electrode diameter gives the smoothest transfer. Too long and the arc wanders and spatters; too short and it sticks. Practice on scrap until the rhythm feels natural.' },
      { type: 'h2', text: '3. Angle matters' },
      { type: 'p', text: 'A 10\u201315\u00b0 drag angle in the direction of travel keeps molten slag behind the puddle. This shields the weld pool and dramatically reduces spatter, especially in horizontal fillets.' },
      { type: 'h2', text: '4. Use fresh, dry electrodes' },
      { type: 'p', text: 'Moisture in the flux boils out as hydrogen gas, which is the leading cause of spatter and porosity. Only take out the electrodes you need and reseal the packet immediately.' },
      { type: 'h2', text: '5. Clean the base metal' },
      { type: 'p', text: 'Paint, oil, rust, and mill scale all vaporise under the arc and blow molten metal around. Two minutes with an angle grinder saves twenty minutes of post-weld cleanup.' },
      { type: 'quote', text: 'The best welders make it look easy because they eliminated every source of trouble before striking the arc.' },
    ],
  },
  {
    id: 'storage-guide',
    category: 'Best Practices',
    author: 'Anita Sharma',
    authorRole: 'Technical Consultant',
    title: 'The right way to store welding electrodes',
    excerpt:
      'Moisture ruins electrodes. Learn how to store, re-dry, and handle your rods to preserve arc quality and shelf life.',
    date: 'May 09, 2025',
    readTime: '5 min read',
    image:
      'https://images.pexels.com/photos/9754818/pexels-photo-9754818.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    content: [
      { type: 'p', text: 'Welding electrodes are precision-engineered consumables, and their performance depends heavily on how they are handled after they leave our factory. Improper storage \u2014 even for a few days \u2014 can render a premium rod as unreliable as a cheap one.' },
      { type: 'h2', text: 'Why moisture is the enemy' },
      { type: 'p', text: 'The flux coating on every electrode contains carefully balanced ingredients. When it absorbs atmospheric moisture, the water breaks down under the arc into hydrogen, which then diffuses into the weld metal. The result: porosity, cracking, and a weld that may look fine but fails under load.' },
      { type: 'h2', text: 'Storage best practices' },
      { type: 'list', items: [
        'Store cartons on wooden pallets, never directly on concrete floors.',
        'Keep the storeroom between 20\u201330 \u00b0C with humidity below 60%.',
        'Open only one packet at a time; reseal or use a portable oven.',
        'Never mix rods from different batches in the same holder.',
        'Rotate stock \u2014 first in, first out.',
      ] },
      { type: 'h2', text: 'When to re-dry' },
      { type: 'p', text: 'If electrodes have been left exposed to humid air for more than a shift, re-dry them before use. For E6013 rods, 100\u2013120 \u00b0C for 1 hour is sufficient. For low-hydrogen rods (E7018), use 300\u2013350 \u00b0C for 1 hour. Never re-dry more than three times \u2014 repeated cycles damage the coating.' },
      { type: 'h2', text: 'On the shop floor' },
      { type: 'p', text: 'Give your welders quivers or portable rod ovens. A ten-rupee habit of keeping rods dry saves thousands of rupees in rework and rejected joints. It is the single highest-leverage change any workshop can make.' },
      { type: 'quote', text: 'A rod is only as good as the last hour it has been stored. Treat it like the precision instrument it is.' },
    ],
  },
];

export const stats = [
  { value: '40+', label: 'Years of Excellence' },
  { value: '5', label: 'Trusted Brands' },
  { value: '10K+', label: 'Loyal Customers' },
  { value: 'ISO', label: 'Certified Quality' },
];

export const products = [
  {
    id: 'cutweld',
    name: 'CUTWELD',
    subtitle: 'E6013 / ER-4211 (IS 814:2004)',
    description:
      'High-quality electrode solutions with maximum strength and reliability. Low smoke, low spatter for cleaner and more efficient welding experiences.',
    features: ['Low Smoke', 'Low Spatter', 'High Tensile Strength', 'IS 814:2004 Certified'],
    image: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/q1beoab8_ChatGPT-Image-Feb-18-2026-10_45_37-AM-1024x683.png',
    imgFit: 'contain',
    color: 'from-amber-500 to-orange-600',
  },
  {
    id: 'weldarc',
    name: 'WELDARC',
    subtitle: 'E6013 Premium Series',
    description:
      'More than 30 years of trust. Delivering outstanding performance with low smoke and spatter for cleaner results. A legacy brand catering to welders since 1985.',
    features: ['Legacy Brand', 'Time-tested', 'Consistent Arc', 'All-position Welding'],
    image: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/jb0o7tv4_image0-1024x683.png',
    imgFit: 'contain',
    color: 'from-orange-500 to-red-600',
  },
  {
    id: 'sunlight',
    name: 'SUNLIGHT-13',
    subtitle: 'General Purpose Electrode',
    description:
      'Tailored welding solutions for every project. From material selection to electrode specifications, our bespoke services ensure optimal results.',
    features: ['Customizable', 'Versatile Use', 'Bright Arc', 'Smooth Finish'],
    image: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/gdjga6k1_PHOTO-2024-10-19-17-48-18-1.jpg',
    imgFit: 'contain',
    color: 'from-yellow-500 to-amber-600',
  },
  {
    id: 'hitkari',
    name: 'HITKARI GOLD',
    subtitle: 'Industrial Grade Electrodes',
    description:
      'Robust solutions tailored for industry-specific requirements. Caters to a variety of welding needs with unmatched durability.',
    features: ['Industrial Grade', 'Heavy-duty', 'Deep Penetration', 'Long Shelf Life'],
    image: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/oqcqi3jr_PHOTO-2024-10-19-17-48-17-1-300x200.jpg',
    imgFit: 'contain',
    color: 'from-amber-600 to-yellow-700',
  },
  {
    id: 'bpl',
    name: 'BPL',
    subtitle: 'Value Series Electrodes',
    description:
      'Reliable performance for everyday welding applications. Trusted by professionals for consistent quality at competitive pricing.',
    features: ['Value-priced', 'Reliable', 'Everyday Use', 'Trade Favorite'],
    image: 'https://images.pexels.com/photos/8940820/pexels-photo-8940820.jpeg?auto=compress&cs=tinysrgb&w=1200',
    imgFit: 'cover',
    color: 'from-stone-500 to-stone-700',
  },
];

export const values = [
  {
    title: 'Quality First',
    desc: 'Every rod meets stringent IS standards. Manufactured with the finest raw materials for exceptional performance.',
    icon: 'ShieldCheck',
  },
  {
    title: 'Continuous Innovation',
    desc: 'We refine our products based on real feedback from welders on the ground, ensuring evolving solutions.',
    icon: 'Lightbulb',
  },
  {
    title: 'Uncompromised Integrity',
    desc: 'Four decades of honest business, transparent pricing, and long-term partnerships built on trust.',
    icon: 'Handshake',
  },
  {
    title: 'Nationwide Reach',
    desc: 'Serving industrial hubs across India — from construction sites to heavy manufacturing plants.',
    icon: 'Truck',
  },
];

export const testimonials = [
  {
    name: 'Raj Kumar',
    role: 'Fabrication Head, Steel Works',
    quote:
      'Prakash Electrodes has consistently provided us with top-of-the-line products that enhance our welding efficiency. Their commitment to quality is unmatched.',
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=200&auto=format&fit=crop',
  },
  {
    name: 'Vikram Singh',
    role: 'Plant Manager, Industrial Solutions',
    quote:
      'The innovative features of their electrodes have transformed our production line, resulting in cleaner and more effective welds. Highly recommended!',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop',
  },
  {
    name: 'Anil Sharma',
    role: 'Contractor, Construction Group',
    quote:
      'Been using WELDARC for over a decade. The arc stability and finish quality are simply the best in the market. Truly a legacy brand.',
    avatar: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=200&auto=format&fit=crop',
  },
];

export const heroImage =
  'https://images.unsplash.com/photo-1698664683348-f9f35b809821?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxhcmMlMjB3ZWxkaW5nfGVufDB8fHx8MTc4NDQ0MzE4Mnww&ixlib=rb-4.1.0&q=85';

export const industries = [
  {
    name: 'Construction',
    desc: 'Structural steel, rebar, and site fabrication.',
    icon: 'Building2',
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&auto=format&fit=crop',
  },
  {
    name: 'Heavy Manufacturing',
    desc: 'Machinery, tanks, boilers, and pressure vessels.',
    icon: 'Factory',
    image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=800&auto=format&fit=crop',
  },
  {
    name: 'Automotive',
    desc: 'Chassis, body panels, and precision components.',
    icon: 'Car',
    image: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?w=800&auto=format&fit=crop',
  },
  {
    name: 'Shipbuilding',
    desc: 'Marine-grade welds for hulls and offshore structures.',
    icon: 'Ship',
    image: 'https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=800&auto=format&fit=crop',
  },
  {
    name: 'Railways',
    desc: 'Rolling stock, tracks, and infrastructure repair.',
    icon: 'Train',
    image: 'https://images.unsplash.com/photo-1474487548417-781cb71495f3?w=800&auto=format&fit=crop',
  },
  {
    name: 'Repair & MRO',
    desc: 'Maintenance, refurbishment, and on-site fabrication.',
    icon: 'Wrench',
    image: 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800&auto=format&fit=crop',
  },
];

export const process = [
  {
    step: '01',
    title: 'Raw Material Sourcing',
    desc: 'Only premium-grade wire rods and flux ingredients from certified Indian mills enter our factory.',
  },
  {
    step: '02',
    title: 'Precision Wire Drawing',
    desc: 'Multi-stage drawing produces uniform diameter cores with tight tolerances for a stable arc.',
  },
  {
    step: '03',
    title: 'Flux Coating',
    desc: 'Extrusion-coated with our proprietary flux formulations for low smoke, low spatter, and clean slag.',
  },
  {
    step: '04',
    title: 'Baking & Curing',
    desc: 'Controlled temperature ovens remove moisture and cure the coating for optimal shelf life.',
  },
  {
    step: '05',
    title: 'Quality Testing',
    desc: 'Every batch is tested for tensile strength, deposition rate, and IS 814:2004 compliance.',
  },
  {
    step: '06',
    title: 'Packaging & Dispatch',
    desc: 'Moisture-proof packaging ensures your electrodes arrive shop-floor ready across India.',
  },
];

export const certifications = [
  {
    name: 'IS 814:2004',
    desc: 'Bureau of Indian Standards',
    logo: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/09nee1ff_ChatGPT-Image-Mar-27-2026-11_43_35-AM.png',
    invertOnDark: true,
  },
  {
    name: 'ISO 9001:2015',
    desc: 'Quality Management System',
    logo: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_electrode-hub/artifacts/u3jrmo6b_ISO_9001-2015.svg.png',
  },
  { name: 'MSME Registered', desc: 'Government of India', icon: 'landmark' },
  { name: '40+ Years', desc: 'Trusted since 1985', icon: 'award' },
];

export const faqs = [
  {
    q: 'Which electrode should I use for mild steel structural welding?',
    a: 'Our WELDARC and CUTWELD E6013 electrodes are ideal for mild-steel structural work \u2014 offering excellent arc stability, smooth bead profile, and reliable strength for beams, columns, and general fabrication.',
  },
  {
    q: 'Do you supply in bulk for large industrial projects?',
    a: 'Yes. We regularly serve steel plants, EPC contractors, and railway workshops. Contact our sales team for volume pricing, custom packaging, and delivery scheduling.',
  },
  {
    q: 'What sizes are available?',
    a: 'Standard diameters of 2.5 mm, 3.15 mm, 4.0 mm, and 5.0 mm in 350 mm and 450 mm lengths, packed in 5 kg and 20 kg cartons. Custom sizes on request.',
  },
  {
    q: 'How should I store the electrodes?',
    a: 'Keep in a dry, well-ventilated place away from moisture. For long storage, re-dry at 100\u2013150\u00b0C for 1\u20132 hours before use to restore optimal arc performance.',
  },
  {
    q: 'Are your products certified?',
    a: 'Yes. All our electrode brands are manufactured in compliance with IS 814:2004 by the Bureau of Indian Standards, and our factory operates under an ISO 9001:2015 quality management system.',
  },
];

export const galleryImages = [
  'https://images.pexels.com/photos/28951817/pexels-photo-28951817.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/17245900/pexels-photo-17245900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxhcmMlMjB3ZWxkaW5nfGVufDB8fHx8MTc4NDQ0MzE4Mnww&ixlib=rb-4.1.0&q=85',
  'https://images.unsplash.com/photo-1609348632802-b952f368fc3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxhcmMlMjB3ZWxkaW5nfGVufDB8fHx8MTc4NDQ0MzE4Mnww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/13296053/pexels-photo-13296053.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/30428852/pexels-photo-30428852.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
];
